<template>
	<div v-if="selectedProduct" class="flex flex-grow flex-col lg:w-auto w-full">
		<div class="flex flex-col gap-4">
			<div class="flex flex-col gap-2.5">
				<div>
					<h1 class="lg:text-2xl text-xl mb-0 font-sans font-medium">
						{{ selectedProduct.VariantName || selectedProduct.Name }}
					</h1>
					<div
						class="text-gray-super-dark"
						v-html="selectedProduct.SubHeader"
					></div>
				</div>
				<div class="flex items-start justify-between gap-4">
					<div class="flex-1 min-w-0">
						<ProductProductAttributesPrice
							:old-price="selectedTotalOldPrice"
							:price="selectedTotalPrice"
							:show-previous="false"
							is-product-page
							show-save-text
						/>
					</div>
					<div class="shrink-0 min-h-[46px]">
						<ProductLipscoreProductRating variant="full" />
					</div>
				</div>
			</div>
			<LazyLoadWrapper>
				<div v-if="hasParametrics" class="grid grid-cols-1 lg:gap-6 gap-4">
					<VariantConfigurator :parametrics="parametrics" />
				</div>
				<ProductBundle v-else-if="isBundleProductType" />
				<div class="flex flex-col gap-4">
					<QuantityBreaks
						v-if="hasBreaks"
						v-model="quantity"
						:options="multipackOptions"
					/>
					<div v-show="!getUser && hasBreaks" class="flex flex-col gap-2">
						<button
							class="text-base flex items-center gap-3"
							type="button"
							@click="openAccountOnDesktop"
						>
							<span>{{ $t("quantity_breaks.login_or_create_account") }}</span>
							<ChevronDownIcon class="w-4 h-4" />
						</button>
					</div>
					<div class="flex gap-3">
						<div class="flex-1 flex flex-col">
							<AddToCart
								:stock="getProductStock(selectedProduct)"
								:quantity="cartQuantity"
								@update:quantity="quantity = $event"
							/>
						</div>
					</div>
					<ul
						v-if="parameticsList.length"
						class="lg:hidden text-sm leading-[19px]"
					>
						<li
							v-for="parametric in parameticsList"
							:key="parametric.Id"
							class="flex justify-between"
						>
							<p class="w-1/2">{{ parametric.Name }}:</p>
							<p class="w-1/2">{{ parametric.Value }}</p>
						</li>
					</ul>
				</div>

				<StoryblokComponent
					v-for="deliveryInfo in blok?.delivery_info"
					:key="deliveryInfo._uid"
					:blok="deliveryInfo"
					class="text-sm text-primary"
				/>
			</LazyLoadWrapper>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type { GroupedParametrics } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import VariantConfigurator from "./VariantConfigurator/VariantConfigurator.vue";
import AddToCart from "./AddToCart/AddToCart.vue";
import QuantityBreaks from "./QuantityBreaks.vue";
import type { ProductAttributes } from "~/types/storyblok/storyblok";
import { ProductTypes } from "~/utils/constants";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import { storeToRefs } from "pinia";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import { PRODUCT_PARAMETRIC_CODES } from "~/templates/breadandboxers/utils/productParametricCodes";
import { toNumber } from "~/templates/breadandboxers/utils/toNumber";
import { useMultipackPricing } from "~/templates/breadandboxers/composables/useMultipackPricing";
import { useProductPricing } from "~/templates/breadandboxers/composables/useProductPricing";

interface Props {
	parametrics?: GroupedParametrics | undefined;
	blok: ProductAttributes;
	mainParametrics?: string | null;
}

const { parametrics, mainParametrics } = defineProps<Props>();

const { getProductStock } = useNorcePwa();
const productStore = useProductStore();
const { selectedProduct, productType, bundleProducts, quantityBreaks } =
	storeToRefs(productStore);
const { oldPrice } = useProductPrices(selectedProduct);
const { loadUser, userHasSession } = useLoadUser();

const { getUser } = storeToRefs(userStore());

const modal = inject<SharedModal>("modal") as SharedModal;

const internalQuantity = ref(1);

const isBundleProductType = computed(
	() =>
		productType.value === ProductTypes.BUNDLE &&
		bundleProducts.value?.length > 0
);
const hasParametrics = computed(
	() => !!parametrics && Object.keys(parametrics).length > 0
);

const baseUnitPrice = computed(() => selectedProduct.value?.PriceIncVat ?? 0);
const productOldUnitPrice = computed(() =>
	oldPrice.value ? Number(oldPrice.value) : null
);

const basePackSize = computed(() => {
	const basePackParametric = selectedProduct.value?.Parametrics?.find(
		({ Code }) => Code === PRODUCT_PARAMETRIC_CODES.BASE_PACK_SIZE
	);
	const packSizeValue = basePackParametric?.Value?.split("-pack")?.[0];
	const parsedPackSize = toNumber(packSizeValue, 1);
	return parsedPackSize > 0 ? parsedPackSize : 1;
});

const { options: multipackOptions } = useMultipackPricing({
	quantityBreaks,
	baseUnitPrice,
	basePackSize,
	productOldUnitPrice
});

const selectedBreak = computed(() =>
	multipackOptions.value.find((option) => option.qtyBreak === quantity.value)
);

const cartQuantity = computed(() => selectedBreak.value?.cartQuantity ?? 1);
const { pricing } = useProductPricing({
	selectedBreak,
	baseUnitPrice,
	productOldUnitPrice,
	cartQuantity
});
const selectedTotalPrice = computed(() => pricing.value.total);
const selectedTotalOldPrice = computed(() =>
	pricing.value.total !== pricing.value.totalOld ? pricing.value.totalOld : null
);
const parameticsList = computed(() => {
	return (
		selectedProduct?.value?.Parametrics?.filter(
			({ Value, IsHidden, Code }) =>
				!!Value && !IsHidden && mainParametrics?.includes(Code)
		).sort(
			(a, b) =>
				mainParametrics?.indexOf(a.Code) - mainParametrics?.indexOf(b.Code)
		) || []
	);
});

const hasBreaks = computed(() => multipackOptions.value.length > 0);

const quantity = computed({
	get() {
		if (!multipackOptions.value.length) {
			return 1;
		}

		const exists = multipackOptions.value.some(
			(option) => option.qtyBreak === internalQuantity.value
		);

		return exists ? internalQuantity.value : multipackOptions.value[0].qtyBreak;
	},
	set(value: number) {
		internalQuantity.value = value;
	}
});

const openAccountOnDesktop = async () => {
	if (!(await userHasSession())) {
		modal.open("AccountLoginForm", {
			position: "side"
		});
	} else {
		await loadUser();
	}
};
</script>
